import React, { useState, useEffect } from 'react';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import { exportDetailedReport } from '../../utils/exportExcel';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Pie, Doughnut } from 'react-chartjs-2';
import { reportService, userService } from '../../services/api';
import './Reports.css';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Reports = () => {
  const [activeTab, setActiveTab] = useState('timesheet');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [timesheetData, setTimesheetData] = useState(null);
  const [timesheetDateRange, setTimesheetDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const [leaveData, setLeaveData] = useState(null);
  const [visaData, setVisaData] = useState(null);
  const [headcountData, setHeadcountData] = useState(null);

  useEffect(() => {
    switch (activeTab) {
      case 'timesheet': fetchTimesheetReport(); break;
      case 'leave': fetchLeaveReport(); break;
      case 'visa': fetchVisaReport(); break;
      case 'headcount': fetchHeadcountReport(); break;
      default: break;
    }
  }, [activeTab]);

  const fetchTimesheetReport = async () => {
    try {
      setLoading(true); setError(null);
      const response = await reportService.getTimesheet({
        startDate: timesheetDateRange.start,
        endDate: timesheetDateRange.end,
      });
      setTimesheetData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load timesheet report');
    } finally { setLoading(false); }
  };

  const fetchLeaveReport = async () => {
    try {
      setLoading(true); setError(null);
      const response = await reportService.getLeave();
      setLeaveData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load leave report');
    } finally { setLoading(false); }
  };

  const fetchVisaReport = async () => {
    try {
      setLoading(true); setError(null);
      const response = await reportService.getVisa();
      setVisaData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load visa report');
    } finally { setLoading(false); }
  };

  const fetchHeadcountReport = async () => {
    try {
      setLoading(true); setError(null);
      const response = await reportService.getHeadcount();
      setHeadcountData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load headcount report');
    } finally { setLoading(false); }
  };

  const getTimesheetChartData = () => {
    if (!timesheetData?.employees) return null;
    return {
      labels: timesheetData.employees.map(item => item.name),
      datasets: [{
        label: 'Hours Worked',
        data: timesheetData.employees.map(item => item.totalHours),
        backgroundColor: 'rgba(37, 99, 235, 0.8)',
        borderColor: 'var(--primary)',
        borderWidth: 1,
        borderRadius: 4,
      }],
    };
  };

  const getLeaveByTypeData = () => {
    if (!leaveData?.byType) return null;
    return {
      labels: leaveData.byType.map(item => item.type),
      datasets: [{
        label: 'Days by Type',
        data: leaveData.byType.map(item => item.count),
        backgroundColor: ['rgba(37,99,235,0.8)', 'rgba(16,185,129,0.8)', 'rgba(245,158,11,0.8)', 'rgba(239,68,68,0.8)', 'rgba(139,92,246,0.8)'],
        borderWidth: 1,
      }],
    };
  };

  const getLeaveByMonthData = () => {
    if (!leaveData?.byMonth) return null;
    return {
      labels: leaveData.byMonth.map(item => item.month),
      datasets: [{
        label: 'Leave Requests',
        data: leaveData.byMonth.map(item => item.count),
        backgroundColor: 'rgba(245, 158, 11, 0.8)',
        borderWidth: 1,
        borderRadius: 4,
      }],
    };
  };

  const getVisaStatusData = () => {
    if (!visaData?.statusCounts) return null;
    return {
      labels: ['Active', 'Expiring Soon', 'Expired'],
      datasets: [{
        data: [visaData.statusCounts.active || 0, visaData.statusCounts.expiring_soon || 0, visaData.statusCounts.expired || 0],
        backgroundColor: ['rgba(16,185,129,0.8)', 'rgba(245,158,11,0.8)', 'rgba(239,68,68,0.8)'],
        borderWidth: 1,
      }],
    };
  };

  const getHeadcountByDepartmentData = () => {
    if (!headcountData?.byDepartment) return null;
    return {
      labels: headcountData.byDepartment.map(item => item.departmentName),
      datasets: [{
        label: 'Employees',
        data: headcountData.byDepartment.map(item => item.count),
        backgroundColor: 'rgba(37, 99, 235, 0.8)',
        borderWidth: 1,
        borderRadius: 4,
      }],
    };
  };

  const getHeadcountByTypeData = () => {
    if (!headcountData?.byType) return null;
    return {
      labels: headcountData.byType.map(item => item.employmentType),
      datasets: [{
        data: headcountData.byType.map(item => item.count),
        backgroundColor: ['rgba(37,99,235,0.8)', 'rgba(16,185,129,0.8)', 'rgba(245,158,11,0.8)', 'rgba(139,92,246,0.8)'],
        borderWidth: 1,
      }],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { position: 'bottom', labels: { font: { size: 12 }, padding: 20, usePointStyle: true } },
    },
  };

  const handleExport = () => {
    exportDetailedReport({ timesheetData, leaveData, visaData, headcountData }, 'HR_Reports');
  };

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h1>Reports</h1>
        <button className="btn btn-secondary" onClick={handleExport}>
          <FiDownload /> Export Excel
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="reports-tabs">
        {['timesheet', 'leave', 'visa', 'headcount'].map(tab => (
          <button key={tab} className={`tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div className="reports-content">
        {/* Timesheet Tab */}
        {activeTab === 'timesheet' && (
          <div className="report-section">
            <div className="section-header">
              <h2>Timesheet Report</h2>
              <div className="date-range">
                <input type="date" value={timesheetDateRange.start} onChange={(e) => setTimesheetDateRange(prev => ({ ...prev, start: e.target.value }))} />
                <span> to </span>
                <input type="date" value={timesheetDateRange.end} onChange={(e) => setTimesheetDateRange(prev => ({ ...prev, end: e.target.value }))} />
                <button className="btn btn-sm btn-primary" onClick={fetchTimesheetReport}><FiRefreshCw /> Refresh</button>
              </div>
            </div>

            {loading ? (
              <div className="loading-spinner"><div className="spinner"></div><p>Loading timesheet data...</p></div>
            ) : timesheetData ? (
              <>
                {timesheetData.employees && timesheetData.employees.length > 0 && (
                  <>
                    <div className="chart-container">
                      <div className="chart-wrapper">
                        <h3>Hours by Employee</h3>
                        <Bar data={getTimesheetChartData()} options={chartOptions} />
                      </div>
                    </div>

                    <div className="stats-row">
                      <div className="stat-card">
                        <span className="stat-label">Total Company Hours</span>
                        <span className="stat-value">{timesheetData.employees.reduce((sum, e) => sum + e.totalHours, 0).toFixed(1)}</span>
                      </div>
                      <div className="stat-card">
                        <span className="stat-label">Employees Tracked</span>
                        <span className="stat-value">{timesheetData.employees.length}</span>
                      </div>
                      <div className="stat-card">
                        <span className="stat-label">Average Hours/Employee</span>
                        <span className="stat-value">
                          {(timesheetData.employees.reduce((sum, e) => sum + e.totalHours, 0) / timesheetData.employees.length).toFixed(1)}
                        </span>
                      </div>
                    </div>

                    <h3>Employee Summary</h3>
                    <div className="table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th>Email</th>
                            <th>Job Title</th>
                            <th>Total Hours</th>
                            <th>Days Worked</th>
                            <th>Avg Hours/Day</th>
                          </tr>
                        </thead>
                        <tbody>
                          {timesheetData.employees.map((emp) => (
                            <tr key={emp.id}>
                              <td>{emp.name}</td>
                              <td>{emp.email}</td>
                              <td>{emp.jobTitle}</td>
                              <td><strong>{emp.totalHours}</strong></td>
                              <td>{emp.daysWorked}</td>
                              <td>{emp.avgHoursPerDay}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {timesheetData.details && timesheetData.details.length > 0 && (
                  <>
                    <h3>Daily Breakdown</h3>
                    <div className="table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Employee</th>
                            <th>Status</th>
                            <th>Total Hours</th>
                            <th>Tasks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {timesheetData.details.map((row, idx) => (
                            <tr key={idx}>
                              <td>{row.date}</td>
                              <td>{row.employeeName}</td>
                              <td><span className={`badge ${row.status === 'approved' ? 'approved' : row.status === 'submitted' ? 'warning' : ''}`}>{row.status}</span></td>
                              <td><strong>{row.totalHours}</strong></td>
                              <td className="tasks-cell">
                                {row.entries && row.entries.length > 0 ? (
                                  <ul className="task-list">
                                    {row.entries.map((entry, i) => (
                                      <li key={i}><strong>{entry.taskName}</strong> — {entry.hours}h {entry.description && <span className="task-desc">({entry.description})</span>}</li>
                                    ))}
                                  </ul>
                                ) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>
        )}

        {/* Leave Tab */}
        {activeTab === 'leave' && (
          <div className="report-section">
            <h2>Leave Report</h2>

            {loading ? (
              <div className="loading-spinner"><div className="spinner"></div><p>Loading leave data...</p></div>
            ) : leaveData ? (
              <>
                <div className="chart-container">
                  {leaveData.byType && leaveData.byType.length > 0 && (
                    <div className="chart-wrapper">
                      <h3>Leave by Type</h3>
                      <Pie data={getLeaveByTypeData()} options={chartOptions} />
                    </div>
                  )}
                  {leaveData.byMonth && leaveData.byMonth.length > 0 && (
                    <div className="chart-wrapper">
                      <h3>Leave by Month</h3>
                      <Bar data={getLeaveByMonthData()} options={chartOptions} />
                    </div>
                  )}
                </div>

                <div className="stats-row">
                  <div className="stat-card">
                    <span className="stat-label">Total Leave Requests</span>
                    <span className="stat-value">{leaveData.byType?.reduce((sum, t) => sum + t.count, 0) || 0}</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-label">Leave Types Used</span>
                    <span className="stat-value">{leaveData.byType?.length || 0}</span>
                  </div>
                </div>

                {leaveData.byEmployee && leaveData.byEmployee.length > 0 && (
                  <>
                    <h3>Per-Employee Summary</h3>
                    <div className="table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th>Email</th>
                            <th>Total Requests</th>
                            <th>Total Days</th>
                            <th>Breakdown by Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leaveData.byEmployee.map((emp, idx) => (
                            <tr key={idx}>
                              <td>{emp.employeeName}</td>
                              <td>{emp.email}</td>
                              <td>{emp.totalRequests}</td>
                              <td><strong>{emp.totalDays}</strong></td>
                              <td>{emp.breakdown}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {leaveData.details && leaveData.details.length > 0 && (
                  <>
                    <h3>All Leave Records</h3>
                    <div className="table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th>Type</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Days</th>
                            <th>Reason</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leaveData.details.map((row, idx) => (
                            <tr key={idx}>
                              <td>{row.employeeName}</td>
                              <td>{row.leaveType}</td>
                              <td>{row.startDate}</td>
                              <td>{row.endDate}</td>
                              <td>{row.daysCount}</td>
                              <td className="reason-cell">{row.reason}</td>
                              <td>
                                <span className={`badge ${row.status === 'approved' ? 'approved' : row.status === 'pending' ? 'warning' : row.status === 'rejected' ? 'expired' : ''}`}>
                                  {row.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>
        )}

        {/* Visa Tab */}
        {activeTab === 'visa' && (
          <div className="report-section">
            <h2>Visa Report</h2>

            {loading ? (
              <div className="loading-spinner"><div className="spinner"></div><p>Loading visa data...</p></div>
            ) : visaData ? (
              <>
                {visaData.statusCounts && (
                  <div className="chart-container">
                    <div className="chart-wrapper">
                      <h3>Visa Status Breakdown</h3>
                      <Doughnut data={getVisaStatusData()} options={chartOptions} />
                    </div>
                  </div>
                )}

                <div className="stats-row">
                  <div className="stat-card">
                    <span className="stat-label">Total Visa Holders</span>
                    <span className="stat-value">
                      {(visaData.statusCounts?.active || 0) + (visaData.statusCounts?.expiring_soon || 0) + (visaData.statusCounts?.expired || 0)}
                    </span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-label">Expiring Soon</span>
                    <span className="stat-value warning">{visaData.statusCounts?.expiring_soon || 0}</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-label">Expired</span>
                    <span className="stat-value danger">{visaData.statusCounts?.expired || 0}</span>
                  </div>
                </div>

                {visaData.expiringList && visaData.expiringList.length > 0 && (
                  <>
                    <h3>Expiring / Expired Visas</h3>
                    <div className="table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th>Email</th>
                            <th>Visa Type</th>
                            <th>Expiry Date</th>
                            <th>Status</th>
                            <th>Country</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visaData.expiringList.map((visa, idx) => (
                            <tr key={idx} className={visa.status === 'expired' ? 'row-danger' : 'row-warning'}>
                              <td>{visa.employeeName}</td>
                              <td>{visa.email}</td>
                              <td>{visa.visaType}</td>
                              <td>{new Date(visa.expiryDate).toLocaleDateString()}</td>
                              <td><span className={`badge ${visa.status === 'expired' ? 'expired' : 'warning'}`}>{visa.status === 'expired' ? 'Expired' : 'Expiring Soon'}</span></td>
                              <td>{visa.countryOfOrigin}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {visaData.allVisas && visaData.allVisas.length > 0 && (
                  <>
                    <h3>All Visa Details</h3>
                    <div className="table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th>Email</th>
                            <th>Job Title</th>
                            <th>Visa Type</th>
                            <th>Issue Date</th>
                            <th>Expiry Date</th>
                            <th>Status</th>
                            <th>Country</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visaData.allVisas.map((visa, idx) => (
                            <tr key={idx}>
                              <td>{visa.employeeName}</td>
                              <td>{visa.email}</td>
                              <td>{visa.jobTitle}</td>
                              <td>{visa.visaType}</td>
                              <td>{visa.issueDate ? new Date(visa.issueDate).toLocaleDateString() : '—'}</td>
                              <td>{new Date(visa.expiryDate).toLocaleDateString()}</td>
                              <td><span className={`badge ${visa.status === 'expired' ? 'expired' : visa.status === 'expiring_soon' ? 'warning' : 'approved'}`}>{visa.status}</span></td>
                              <td>{visa.countryOfOrigin}</td>
                              <td className="reason-cell">{visa.notes || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>
        )}

        {/* Headcount Tab */}
        {activeTab === 'headcount' && (
          <div className="report-section">
            <h2>Headcount Report</h2>

            {loading ? (
              <div className="loading-spinner"><div className="spinner"></div><p>Loading headcount data...</p></div>
            ) : headcountData ? (
              <>
                <div className="chart-container">
                  {headcountData.byDepartment && headcountData.byDepartment.length > 0 && (
                    <div className="chart-wrapper">
                      <h3>Employees by Department</h3>
                      <Bar data={getHeadcountByDepartmentData()} options={chartOptions} />
                    </div>
                  )}
                  {headcountData.byType && headcountData.byType.length > 0 && (
                    <div className="chart-wrapper">
                      <h3>By Employment Type</h3>
                      <Pie data={getHeadcountByTypeData()} options={chartOptions} />
                    </div>
                  )}
                </div>

                <div className="stats-row">
                  <div className="stat-card">
                    <span className="stat-label">Total Headcount</span>
                    <span className="stat-value">{headcountData.byDepartment?.reduce((sum, d) => sum + d.count, 0) || 0}</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-label">Departments</span>
                    <span className="stat-value">{headcountData.byDepartment?.length || 0}</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-label">Employment Types</span>
                    <span className="stat-value">{headcountData.byType?.length || 0}</span>
                  </div>
                </div>

                {headcountData.roster && headcountData.roster.length > 0 && (
                  <>
                    <h3>Full Employee Roster</h3>
                    <div className="table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Department</th>
                            <th>Job Title</th>
                            <th>Employment Type</th>
                            <th>Join Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {headcountData.roster.map((emp, idx) => (
                            <tr key={idx}>
                              <td>{emp.name}</td>
                              <td>{emp.email}</td>
                              <td>{emp.department}</td>
                              <td>{emp.jobTitle}</td>
                              <td>{emp.employmentType}</td>
                              <td>{emp.joinDate ? new Date(emp.joinDate).toLocaleDateString() : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
